import type { HTTPClient } from '@abaco/web-common/src/HTTP';
import {
    translateRawGabData,
} from "../utilities/helpers";
import type {
    GabDictionary,
    GabHeader,
    GabHeaderRaw,
    GabBooking,
    GabBookingRaw,
    PremiseItem,
    DestinationItem,
    AddGabHeaderRequest,
    GetCalendarByRangeRequest,
} from '../models/GabModule';

export default class GabApi {
    private http: HTTPClient;

    constructor(http: HTTPClient) {
        this.http = http;
    }

    /**
     *
     */
    public async getGabCodes(): Promise<GabDictionary | null> {
        return (await this.http.get("/gab-api/api/v1/code/codes")).data;
    }

    /**
     * Get GAB element by id
     * @param id
     */
    public async getGabHeader(id = ""): Promise<GabHeaderRaw | null> {
        return (await this.http.get("/gab-api/api/v1/gab/" + id)).data;
    }

    /**
     * Add GAB element (GabRequest + GabBooking)
     * @param body
     */
    public async addGabHeader(body: AddGabHeaderRequest): Promise<GabHeaderRaw | null> {
        return (await this.http.post("/gab-api/api/v1/gab/", body)).data;
    }

    /**
     *
     * @param id
     */
    public async deleteGabHeader(id: number) {
        return this.http.delete("/gab-api/api/v1/gab/" + id);
    }

    /**
     *
     * @param id
     * @param license_plate
     */
    public async editPlateGabHeader(id: number, license_plate: string) {
        return this.http.post("/gab-api/api/v1/gab/" + id + "/licensePlate", {
            license_plate: license_plate
        });
    }

    /**
     *
     * @param id
     */
    public async notDeliveredGabHeader(id: number) {
        return this.http.get("/gab-api/api/v1/gab/" + id + "/markAsNotDelivered");
    }

    /**
     * Get calendar of GabBookings in the requested range.
     * Days without bookings will be omitted.
     *
     * @param body
     */
    public async getRawCalendarByRange(body: GetCalendarByRangeRequest): Promise<GabBookingRaw[] | null> {
        return (await this.http.post("/gab-api/api/v1/gab/calendar", body)).data;
    }

    /**
     * getRawCalendarByRange but with refined data
     * @param body
     */
    public async getCalendarByRange(body: GetCalendarByRangeRequest): Promise<{
        headers: Record<string, GabHeader>;
        bookings: Record<string, GabBooking>;
    }> {
        return this.getRawCalendarByRange(body)
            .then((elements: GabBookingRaw[] | null) => {
                const headers: Record<string, GabHeader> = {};
                const bookings: Record<string, GabBooking> = {};
                if(!elements)
                    return {
                        headers,
                        bookings,
                    };
                for(let i = elements.length; i--; ){
                    const [ tempBooking, tempHeaders = [] ] = translateRawGabData(elements[i]);
                    bookings[tempBooking.book_id] = tempBooking;
                    for(let h = tempHeaders.length; h--; )
                        headers[tempHeaders[h].request_id] = tempHeaders[h];
                }
                return {
                    headers,
                    bookings,
                };
            });
    }

    /**
     * List of premises (stables) of farm
     * @param id
     */
    public async getPremises(id: number): Promise<Record<number, PremiseItem>> {
        return this.http.get("/gab-api/api/v1/code/premises?farmId=" + id)
            .then(({ data = [] }: { data: PremiseItem[] }) => {
                const records: Record<number, PremiseItem> = {};
                for(let i = data.length; i--; )
                    records[data[i].pkid] = data[i];
                return records;
            });
    }

    /**
     * List of possible destinations (companies)
     */
    public async getDestinations(): Promise<Record<number, DestinationItem>> {
        return this.http.get("/gab-api/api/v1/code/destinations")
            .then(({ data = [] }: { data: DestinationItem[] }) => {
                const records: Record<number, DestinationItem> = {};
                for(let i = data.length; i--; )
                    records[data[i].pk_cuaa] = data[i];
                return records;
            });
    }
}
